/**
 * Recording Player Route — embedded Vidstack player for community_recordings
 * (Wunderbuild, VA Sync, future sources). Source-agnostic sibling of the events
 * recording surface; opened from a post's "▶ Watch recording" link.
 *
 * Layout mirrors the events recording surface (pages/EventDetail RecordingSection):
 * an in-flow page inside the app shell — a titled 16:9 player wrapped in the same
 * `rounded-[14px] bg-card border` frame — NOT a fullscreen overlay.
 *
 * TWO PLAYBACK PATHS (ticket 86d40p593). `recording-stream?action=token` runs
 * the membership check and then answers in one of two shapes:
 *
 *   mode 'proxy'  — the recording has no stream_uid. We build the same
 *                   ?action=stream URL as before and bytes are Range-proxied
 *                   through the edge function. Unchanged behaviour.
 *   mode 'stream' — the recording has been migrated to Cloudflare Stream. The
 *                   server returns a complete playback address carrying a signed
 *                   pass, and the browser talks to Cloudflare directly.
 *
 * The pass expires. Renewal happens twice over: quietly on a timer at 80% of
 * the pass's own stated life, and reactively when the player reports a failure
 * because a sleeping laptop froze that timer. Both re-run the membership check
 * server-side, so a member who lost access is stopped rather than renewed.
 *
 * That whole mechanism now lives in `useStreamPass`, extracted 14 Sep because
 * four more surfaces need it — lessons, events, chat and masterminds. This
 * route was the only place it had ever existed. What stays here is the part
 * that is genuinely this route's own: which endpoint to ask, and how to put the
 * viewer back where they were once a new source arrives, which only the owner
 * of the player instance can know.
 */

import { createFileRoute } from '@tanstack/react-router';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  isHLSProvider,
  MediaPlayer,
  MediaPlayerInstance,
  MediaProvider,
  MediaProviderAdapter,
} from '@vidstack/react';
import { DefaultVideoLayout, defaultLayoutIcons } from '@vidstack/react/player/layouts/default';
import Hls from 'hls.js';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useTrackEntityAction } from '@/hooks/use-entity-tracker';
import { BASE_HLS_CONFIG } from '@/components/media/hls-config';
import { useStreamPass } from '@/hooks/use-stream-pass';

export const Route = createFileRoute('/recording/$recordingId' as any)({
  component: RecordingPlayerPage,
});

function RecordingPlayerPage() {
  const { recordingId } = Route.useParams() as { recordingId: string };
  const [title, setTitle] = useState<string>('Recording');

  // Playback tracking → activities (entity_type='recording'), which feeds the
  // admin_analytics_recordings section of the Ask-the-data bundle. One play
  // event per page visit — first `play` only, not pause/resume.
  const trackAction = useTrackEntityAction();
  const hasTrackedPlayRef = useRef(false);

  const playerRef = useRef<MediaPlayerInstance>(null);
  const resumeAtRef = useRef<number | null>(null);
  const wasPlayingRef = useRef(false);
  const hasSeekRef = useRef(false);

  /**
   * Capture the viewer's place immediately before a new pass is swapped in.
   * The hook calls this at the right moment; restoring it is `onCanPlay` below.
   */
  const captureBeforeSwap = useCallback(() => {
    resumeAtRef.current = playerRef.current?.currentTime ?? null;
    wasPlayingRef.current = playerRef.current?.paused === false;
  }, []);

  const { source, error, renewOnError } = useStreamPass({
    resourceId: recordingId,
    tokenUrl: (base) =>
      `${base}/functions/v1/recording-stream?action=token&id=${encodeURIComponent(recordingId)}`,
    proxyUrl: (base, token) =>
      `${base}/functions/v1/recording-stream?action=stream&id=${encodeURIComponent(
        recordingId,
      )}&t=${encodeURIComponent(token)}`,
    onBeforeSwap: captureBeforeSwap,
  });

  // A new source means a fresh seek is owed. Previously this was set inside the
  // renewal itself; as an effect on `source` it fires at the same moment and
  // covers the first load too.
  useEffect(() => {
    hasSeekRef.current = false;
  }, [source]);

  // Recording metadata for the header (RLS tier-gated select). Best-effort;
  // never blocks playback. Cast: community_recordings isn't in the FE's
  // generated Database types.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const { data: rec } = await (supabase as any)
        .from('community_recordings')
        .select('name')
        .eq('id', recordingId)
        .maybeSingle();
      if (!cancelled && rec?.name) setTitle(rec.name as string);
    })();
    return () => {
      cancelled = true;
    };
  }, [recordingId]);

  /**
   * Stream serves HLS, so give it our shared tuning — but never the cache-bust
   * loader. That loader makes every request unique by design, which against
   * Cloudflare forces a cache miss on every segment for every viewer.
   */
  const onProviderChange = useCallback((provider: MediaProviderAdapter | null) => {
    if (!provider || !isHLSProvider(provider)) return;
    provider.library = Hls;
    provider.config = { ...BASE_HLS_CONFIG };
  }, []);

  /**
   * Restore the viewer's place AND their playback state after a source swap.
   * Changing `src` makes the player reload from the start, paused. Seeking back
   * is not enough — without the play() a renewal silently stops a video someone
   * was watching, which at an hour-long pass happens in the middle of most long
   * recordings rather than never.
   */
  const onCanPlay = useCallback(() => {
    const player = playerRef.current;
    if (!player) return;
    const resumeAt = resumeAtRef.current;
    if (resumeAt && resumeAt > 0 && !hasSeekRef.current) {
      player.currentTime = resumeAt;
      hasSeekRef.current = true;
      resumeAtRef.current = null;
    }
    if (wasPlayingRef.current) {
      wasPlayingRef.current = false;
      // The viewer already gestured to start this video, so autoplay policy is
      // satisfied. Swallow the rejection anyway — a refused resume should leave
      // them looking at a paused player, not an error screen.
      void player.play().catch(() => {});
    }
  }, []);

  /**
   * The player only tells us something went wrong, not why — and hls.js has
   * already exhausted its own fragment retries by the time this fires. Rather
   * than guess at Cloudflare's refusal code, which they do not document, ask
   * our own edge function for a fresh pass. It is the only thing that knows
   * whether this member is still entitled, and its answer is what we branch on.
   */

  return (
    <div className="flex-1 min-h-0 overflow-y-auto bg-background">
      <div className="mx-auto w-full max-w-4xl px-4 sm:px-6 py-6">
        <button
          type="button"
          onClick={() => window.history.back()}
          className="mb-4 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>

        <div className="mb-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Recording
        </div>
        <h1 className="mb-4 text-xl font-semibold text-foreground sm:text-2xl">{title}</h1>

        <div className="overflow-hidden rounded-[14px] border border-border bg-card">
          {error ? (
            <div className="flex aspect-video w-full items-center justify-center px-6 text-center">
              <div>
                <p className="text-sm font-medium text-foreground">Couldn't load recording</p>
                <p className="mt-1 text-xs text-muted-foreground">{error}</p>
              </div>
            </div>
          ) : !source ? (
            <div className="flex aspect-video w-full items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <MediaPlayer
              ref={playerRef}
              src={source}
              aspectRatio="16/9"
              playsInline
              className="w-full"
              onProviderChange={onProviderChange}
              onCanPlay={onCanPlay}
              onError={renewOnError}
              onPlay={() => {
                if (hasTrackedPlayRef.current) return;
                hasTrackedPlayRef.current = true;
                trackAction('viewed', 'recording', recordingId, title, {
                  source: 'community_recording',
                });
              }}
            >
              <MediaProvider />
              <DefaultVideoLayout icons={defaultLayoutIcons} />
            </MediaPlayer>
          )}
        </div>
      </div>
    </div>
  );
}
